import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { updateUserInfo } from '../../../features/user/userSlice'; // adjust path if needed
import './Health.css';

import {
  HeartPulse,
  Activity,
  Wind,
  Flame,
  FileText,
  Watch,
  Smartphone,
  Check,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
  Thermometer,
  Footprints,
  ShieldCheck,
  AlertTriangle,
  X,
  Loader2,
  Gauge,
  UploadCloud,
  Trash2,
  ExternalLink,
} from 'lucide-react';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  AreaChart,
  Area,
} from 'recharts';
import config from '../../../config';
import CoupleGeneticTab from './CoupleGeneticTab';
import { goalHasFeature } from '../../../constants/familyPlanningGoals';

// ============================================================================
// API CONFIG
// ============================================================================

// The login token lives in localStorage ("remember me") or sessionStorage
// (normal login) — see userSlice.js. Reading only localStorage made the page
// lose the user whenever the session was in sessionStorage.
function getAuthToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
}

// Pulls the real logged-in patient's ID out of their JWT.
// Returns null when there's no usable id, and every caller below refuses to
// make the request at all in that case (no hardcoded fallback user).
function getCurrentUserId() {
  const token = getAuthToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const id = payload.id || payload._id;
    return id ? String(id) : null;
  } catch (err) {
    console.warn('Health.jsx: failed to decode auth token', err);
    return null;
  }
}

class NotSignedInError extends Error {
  constructor() {
    super('Please sign in to view this.');
    this.name = 'NotSignedInError';
  }
}

async function apiRequest(path, options = {}) {
  const userId = getCurrentUserId();
  if (!userId) throw new NotSignedInError();

  const url = `${config.baseURL}/api/health-records${path}${path.includes('?') ? '&' : '?'}user_id=${userId}`;

  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });

  const body = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(body.error || `Request failed (${res.status})`);
  }

  return body;
}

// ---------------------------------------------------------------------------
// Reports API — separate base path (/api/health-reports), same user_id
// query-param convention as the rest of this page.
// ---------------------------------------------------------------------------

async function fetchReports() {
  const userId = getCurrentUserId();
  if (!userId) throw new NotSignedInError();

  const res = await fetch(`${config.baseURL}/api/health-reports?user_id=${userId}`);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body.data || [];
}

async function uploadReport(file) {
  const userId = getCurrentUserId();
  if (!userId) throw new NotSignedInError();

  const formData = new FormData();
  formData.append('report', file);
  formData.append('userId', userId);

  const res = await fetch(`${config.baseURL}/api/health-reports/upload`, {
    method: 'POST',
    body: formData, // don't set Content-Type — the browser sets the multipart boundary
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Upload failed (${res.status})`);
  return body.data;
}

async function deleteReport(id) {
  const userId = getCurrentUserId();
  if (!userId) throw new NotSignedInError();

  const res = await fetch(`${config.baseURL}/api/health-reports/${id}?user_id=${userId}`, {
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 204) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Delete failed (${res.status})`);
  }
}

// ============================================================================
// METRIC CONFIG
// ============================================================================

const METRICS = [
  {
    id: 'heart_rate',
    label: 'Heart Rate',
    shortLabel: 'HR Sync',
    unit: 'bpm',
    icon: HeartPulse,
    color: '#D85A30',
    decimals: 0,
    description: 'Average resting / daily heart rate',
  },
  {
    id: 'respiratory_rate',
    label: 'Respiratory Rate',
    shortLabel: 'RR Sync',
    unit: 'brpm',
    icon: Wind,
    color: '#185FA5',
    decimals: 1,
    description: 'Average breaths per minute',
  },
  {
    id: 'hrv',
    label: 'Heart Rate Variability',
    shortLabel: 'HRV Sync',
    unit: 'ms',
    icon: Activity,
    color: '#0F6E56',
    decimals: 1,
    description: 'Heart rate variability',
  },
  {
    id: 'skin_temperature',
    label: 'Skin Temperature',
    shortLabel: 'Skin Temp',
    unit: '°C',
    icon: Thermometer,
    color: '#E76F51',
    decimals: 2,
    description: 'Wrist / skin temperature',
  },
  {
    id: 'movement',
    label: 'Movement',
    shortLabel: 'Movement',
    unit: 'steps',
    icon: Footprints,
    color: '#2A9D8F',
    decimals: 0,
    description: 'Daily movement and steps',
  },
  {
    id: 'active_energy',
    label: 'Active Energy',
    shortLabel: 'Energy',
    unit: 'kcal',
    icon: Flame,
    color: '#8B5CF6',
    decimals: 0,
    description: 'Daily active energy',
  },
  {
    id: 'blood_pressure',
    label: 'Blood Pressure',
    shortLabel: 'BP',
    unit: 'mmHg',
    icon: Gauge,
    color: '#B3261E',
    decimals: 0,
    description: 'Systolic / diastolic blood pressure',
    isBloodPressure: true,
    secondaryKey: 'blood_pressure_diastolic',
  },
];

// Every numeric key that appears on a data row (used when aggregating).
const AGGREGATE_KEYS = [...METRICS.map(m => m.id), 'blood_pressure_diastolic'];

// ============================================================================
// DATE RANGE CONFIG
// ============================================================================
// Only three quick presets are shown (7 days, 1 month, 3 months) plus Custom.
// Presets are "how far back from today" using real calendar months. Anything
// longer (4 months, 1 year, 10 years … up to MAX_RANGE_YEARS) is picked with
// the Custom range (any from/to date, or "last N days / months / years").

const MAX_RANGE_YEARS = 30;

const RANGE_PRESETS = [
  { id: '7d', label: '7 days', unit: 'days', amount: 7 },
  { id: '1m', label: '1 month', unit: 'months', amount: 1 },
  { id: '3m', label: '3 months', unit: 'months', amount: 3 },
];

const CUSTOM_RANGE_ID = 'custom';
const QUICK_UNITS = [
  { id: 'days', label: 'days' },
  { id: 'months', label: 'months' },
  { id: 'years', label: 'years' },
];

// Rows rendered per "page" in the long lists/tables (30 years of daily data
// would otherwise be ~11,000 DOM rows).
const LIST_PAGE_SIZE = 100;

// ============================================================================
// DEVICES
// ============================================================================
// There is no dedicated devices/connections endpoint, so "connected" is
// inferred from the one real signal this page has: whether there's any synced
// reading at all (see DevicesTab below, which takes `hasData`).

const DEVICE_CATALOG = [
  {
    id: 'watch',
    name: 'Smart Watch',
    desc: 'Heart rate, HRV, breathing, temperature and activity',
    icon: Watch,
  },
  {
    id: 'iphone',
    name: 'iPhone Health',
    desc: 'Receives Smart Watch HealthKit data',
    icon: Smartphone,
  },
];

// ============================================================================
// TABS
// ============================================================================

const COUPLE_DNA_TAB = 'Couple DNA & Birth Risk';
const TABS = ['Overview', 'Timeline', 'Reports', COUPLE_DNA_TAB];

// ============================================================================
// TRAJECTORY LABELS
// ============================================================================

const TRAJECTORY_LABELS = {
  regulation: 'Regulation',
  repair: 'Recovery',
  integration: 'Daily Integration',
};

// ============================================================================
// DATE HELPERS
// ============================================================================

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Local-time YYYY-MM-DD (toISOString would shift the day for UTC+05:30 etc.)
function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseISODate(iso) {
  return new Date(`${String(iso).slice(0, 10)}T00:00:00`);
}

function daysBetweenInclusive(fromIso, toIso) {
  const diff = Math.round((parseISODate(toIso) - parseISODate(fromIso)) / MS_PER_DAY);
  return diff + 1;
}

// today minus N days / months / years (real calendar math), clamped to the max.
function subtractFromDate(base, unit, amount) {
  const d = new Date(base);
  if (unit === 'days') d.setDate(d.getDate() - (amount - 1)); // "7 days" includes today
  if (unit === 'months') d.setMonth(d.getMonth() - amount);
  if (unit === 'years') d.setFullYear(d.getFullYear() - amount);
  return d;
}

function earliestAllowedDate() {
  const d = new Date();
  d.setFullYear(d.getFullYear() - MAX_RANGE_YEARS);
  return toISODate(d);
}

// Builds { from, to, days (back from today, for the API), totalDays (in range) }.
function buildRange(selection) {
  const today = new Date();
  const todayIso = toISODate(today);
  const floorIso = earliestAllowedDate();

  let from;
  let to = todayIso;

  if (selection.id === CUSTOM_RANGE_ID) {
    from = selection.from || todayIso;
    to = selection.to || todayIso;
  } else {
    const preset = RANGE_PRESETS.find(p => p.id === selection.id) || RANGE_PRESETS[0];
    from = toISODate(subtractFromDate(today, preset.unit, preset.amount));
  }

  if (from < floorIso) from = floorIso;
  if (to > todayIso) to = todayIso;
  if (from > to) from = to;

  return {
    from,
    to,
    // The summary endpoint takes `days` counted back from today, so cover
    // everything from `from` to now and trim to `to` on the client.
    days: daysBetweenInclusive(from, todayIso),
    totalDays: daysBetweenInclusive(from, to),
  };
}

// Day / week / month buckets so a 30-year range stays readable and fast.
function pickGranularity(totalDays) {
  if (totalDays <= 120) return 'day';
  if (totalDays <= 1100) return 'week'; // ~3 years
  return 'month';
}

const GRANULARITY_LABELS = { day: 'Daily', week: 'Weekly average', month: 'Monthly average' };

function bucketStart(iso, granularity) {
  const d = parseISODate(iso);
  if (granularity === 'week') {
    const dow = (d.getDay() + 6) % 7; // Monday = 0
    d.setDate(d.getDate() - dow);
    return toISODate(d);
  }
  if (granularity === 'month') {
    return toISODate(new Date(d.getFullYear(), d.getMonth(), 1));
  }
  return String(iso).slice(0, 10);
}

// Averages every metric inside each bucket (missing values are ignored).
function aggregateData(data, granularity) {
  if (granularity === 'day') return data;

  const buckets = new Map();

  data.forEach(row => {
    const key = bucketStart(row.date, granularity);
    if (!buckets.has(key)) buckets.set(key, { date: key, _sums: {}, _counts: {} });
    const bucket = buckets.get(key);

    AGGREGATE_KEYS.forEach(k => {
      const v = Number(row[k]);
      if (row[k] !== null && row[k] !== undefined && Number.isFinite(v)) {
        bucket._sums[k] = (bucket._sums[k] || 0) + v;
        bucket._counts[k] = (bucket._counts[k] || 0) + 1;
      }
    });
  });

  return Array.from(buckets.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(bucket => {
      const row = { date: bucket.date };
      AGGREGATE_KEYS.forEach(k => {
        row[k] = bucket._counts[k]
          ? Number((bucket._sums[k] / bucket._counts[k]).toFixed(2))
          : undefined;
      });
      return row;
    });
}

// ============================================================================
// FORMAT HELPERS
// ============================================================================

function formatDate(date) {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

// Axis/tooltip label that adds the year once the range is long enough to need it.
function formatBucketLabel(date, granularity) {
  const d = new Date(`${date}T00:00:00`);
  if (granularity === 'month') {
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  if (granularity === 'week') {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  }
  return formatDate(date);
}

function formatLongDate(date) {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatRangeDate(date) {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatUploadedAt(iso) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes)) return '--';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatValue(metric, value) {
  if (!Number.isFinite(value)) return '--';

  if (metric.decimals > 0) {
    return Number(value).toFixed(metric.decimals);
  }

  return Math.round(value).toString();
}

// For blood_pressure: renders "120/80" from a data row, falling back to '--'.
function formatBloodPressure(metric, row) {
  if (!row) return '--';

  const systolic = row[metric.id];
  const diastolic = row[metric.secondaryKey];

  if (!Number.isFinite(systolic)) return '--';
  if (!Number.isFinite(diastolic)) return `${Math.round(systolic)}`;

  return `${Math.round(systolic)}/${Math.round(diastolic)}`;
}

function calculateAverage(data, key) {
  if (!data.length) return null;

  const values = data.map(item => Number(item[key])).filter(Number.isFinite);

  if (!values.length) return null;

  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

// Coverage = days with a reading / days in the selected range. Passing
// `totalDays` makes this honest for long ranges (a 30-year range with a year
// of data is ~3% covered, not 100%).
function calculateCoverage(data, key, totalDays) {
  const denominator = totalDays || data.length;

  if (!denominator) {
    return { percentage: 0, label: 'low' };
  }

  const available = data.filter(item => Number.isFinite(Number(item[key]))).length;

  const percentage = Math.min(100, Math.round((available / denominator) * 100));

  let label = 'low';

  if (percentage >= 90) {
    label = 'high';
  } else if (percentage >= 60) {
    label = 'medium';
  }

  return { percentage, label };
}

function calculateTrend(data, key) {
  const values = data.map(item => Number(item[key])).filter(Number.isFinite);

  if (values.length < 2) {
    return {
      delta: 0,
      direction: 'flat',
      latest: values[0] ?? null,
    };
  }

  const latest = values[values.length - 1];
  const previous = values[values.length - 2];

  const delta = Number((latest - previous).toFixed(2));

  return {
    delta,
    direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
    latest,
  };
}

function coverageTone(label) {
  if (label === 'high') return { color: '#0F6E56', text: 'Good coverage' };
  if (label === 'medium') return { color: '#B4780A', text: 'Partial coverage' };
  return { color: '#B3261E', text: 'Low coverage' };
}

function trajectoryTone(state) {
  if (state === 'improving') return { color: '#0F6E56', label: 'Improving' };
  if (state === 'draining') return { color: '#B3261E', label: 'Draining' };
  if (state === 'stable') return { color: '#185FA5', label: 'Stable' };
  return { color: '#888780', label: 'Not enough data' };
}

// Client-side heads-up only — the real, authoritative alert is fired by
// alertService.js on the backend the moment a reading is POSTed.
function localAlertCheck(metric, row) {
  if (!row) return null;

  if (metric.isBloodPressure) {
    const systolic = row[metric.id];
    const diastolic = row[metric.secondaryKey];
    if (!Number.isFinite(systolic) || !Number.isFinite(diastolic)) return null;

    if (systolic >= 180 || diastolic >= 120) {
      return { severity: 'critical', message: `Hypertensive crisis: ${systolic}/${diastolic} mmHg` };
    }
    if (systolic >= 140 || diastolic >= 90) {
      return { severity: 'high', message: `Elevated blood pressure: ${systolic}/${diastolic} mmHg` };
    }
    return null;
  }

  if (metric.id === 'heart_rate') {
    const value = row[metric.id];
    if (!Number.isFinite(value)) return null;
    if (value >= 150) return { severity: 'critical', message: `Heart rate critically high: ${value} bpm` };
    if (value >= 130) return { severity: 'high', message: `Heart rate above normal range: ${value} bpm` };
  }

  return null;
}

// ============================================================================
// DATA HOOK
// ============================================================================

function useHealthData(days) {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // No fabricated data: a real empty result stays empty, NotSignedInError
  // shows "please sign in", and a genuine fetch failure shows the real error.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const json = await apiRequest(`/summary?days=${days}`);
      const rows = Array.isArray(json.data) ? json.data : [];
      setData(rows);
      setSummary(json.summary || null);
    } catch (err) {
      console.error('Health summary fetch failed:', err);
      setError(err instanceof NotSignedInError ? err.message : (err.message || 'Could not load health data'));
      setData([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, summary, loading, error, refetch: load };
}

// ============================================================================
// ALERT BANNER
// ============================================================================

function AlertBanner({ alert, onDismiss }) {
  if (!alert) return null;

  const isCritical = alert.severity === 'critical';

  return (
    <div
      className="chart-card"
      style={{
        borderColor: isCritical ? '#B3261E' : '#B4780A',
        backgroundColor: isCritical ? '#B3261E0D' : '#B4780A0D',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <AlertTriangle
          size={18}
          color={isCritical ? '#B3261E' : '#B4780A'}
          style={{ flexShrink: 0, marginTop: 2 }}
        />

        <div>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: isCritical ? '#B3261E' : '#B4780A' }}>
            {isCritical ? 'Critical alert sent to your doctor' : 'Alert sent to your doctor'}
          </p>

          <p style={{ margin: '3px 0 0', fontSize: 13, color: '#3A3A38' }}>{alert.message}</p>
        </div>
      </div>

      <button
        onClick={onDismiss}
        aria-label="Dismiss alert"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: '#888780',
          padding: 4,
          flexShrink: 0,
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}

// ============================================================================
// QUICK LOG BLOOD PRESSURE
// ============================================================================

function QuickLogBloodPressure({ onLogged }) {
  const [systolic, setSystolic] = useState('');
  const [diastolic, setDiastolic] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const handleSubmit = async e => {
    e.preventDefault();
    setFormError(null);

    if (!systolic || !diastolic) {
      setFormError('Enter both systolic and diastolic values.');
      return;
    }

    setSubmitting(true);

    try {
      const json = await apiRequest('/', {
        method: 'POST',
        body: JSON.stringify({
          metric: 'blood_pressure',
          value: Number(systolic),
          secondary: Number(diastolic),
          recordedDate: toISODate(new Date()),
          source: 'manual',
        }),
      });

      setSystolic('');
      setDiastolic('');
      onLogged(json.alert || null);
    } catch (err) {
      setFormError(err.message || 'Failed to log reading');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div>
          <h3 className="chart-title">Log a blood pressure reading</h3>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: '#888780' }}>
            Out-of-range readings notify your doctor instantly.
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}
      >
        <div>
          <label style={{ display: 'block', fontSize: 11, color: '#777', marginBottom: 4 }}>
            Systolic
          </label>
          <input
            type="number"
            value={systolic}
            onChange={e => setSystolic(e.target.value)}
            placeholder="120"
            style={{
              width: 90,
              padding: '8px 10px',
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              fontSize: 14,
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 11, color: '#777', marginBottom: 4 }}>
            Diastolic
          </label>
          <input
            type="number"
            value={diastolic}
            onChange={e => setDiastolic(e.target.value)}
            placeholder="80"
            style={{
              width: 90,
              padding: '8px 10px',
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              fontSize: 14,
            }}
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          style={{
            padding: '9px 16px',
            borderRadius: 8,
            border: 'none',
            background: '#0F4C75',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: submitting ? 'default' : 'pointer',
            opacity: submitting ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {submitting && <Loader2 size={14} style={{ animation: 'health-spin 1s linear infinite' }} />}
          {submitting ? 'Logging...' : 'Log reading'}
        </button>

        {formError && (
          <span style={{ fontSize: 12, color: '#B3261E' }}>{formError}</span>
        )}
      </form>
    </div>
  );
}

// ============================================================================
// DATE RANGE SELECTOR
// ============================================================================
// Preset chips (7 days … 30 years) + a Custom panel with:
//   • exact From / To dates
//   • "Last [N] [days|months|years]" for any N (e.g. last 18 months)
// `selection` is { id, from?, to? }; `onChange` receives the same shape.

const rangeInputStyle = {
  padding: '7px 9px',
  borderRadius: 8,
  border: '1px solid #E5E7EB',
  fontSize: 13,
  background: '#fff',
};

function RangeSelector({ selection, range, onChange }) {
  const todayIso = toISODate(new Date());
  const minIso = earliestAllowedDate();

  const [draftFrom, setDraftFrom] = useState(range.from);
  const [draftTo, setDraftTo] = useState(range.to);
  const [quickAmount, setQuickAmount] = useState('18');
  const [quickUnit, setQuickUnit] = useState('months');
  const [rangeError, setRangeError] = useState(null);

  const isCustom = selection.id === CUSTOM_RANGE_ID;

  // Keep the date inputs in step when a preset (or quick range) is applied.
  useEffect(() => {
    setDraftFrom(range.from);
    setDraftTo(range.to);
  }, [range.from, range.to]);

  const accent = { borderColor: '#0F4C75', color: '#0F4C75', backgroundColor: '#0F4C750D' };

  function applyCustom(from, to) {
    if (!from || !to) {
      setRangeError('Pick both a start and an end date.');
      return;
    }
    if (from > to) {
      setRangeError('Start date must be on or before the end date.');
      return;
    }
    if (from < minIso) {
      setRangeError(`Start date can't be more than ${MAX_RANGE_YEARS} years ago.`);
      return;
    }
    if (to > todayIso) {
      setRangeError("End date can't be in the future.");
      return;
    }
    setRangeError(null);
    onChange({ id: CUSTOM_RANGE_ID, from, to });
  }

  function applyQuick() {
    const amount = Math.floor(Number(quickAmount));
    if (!Number.isFinite(amount) || amount < 1) {
      setRangeError('Enter a number of 1 or more.');
      return;
    }
    const from = toISODate(subtractFromDate(new Date(), quickUnit, amount));
    if (from < minIso) {
      setRangeError(`The maximum range is ${MAX_RANGE_YEARS} years.`);
      return;
    }
    applyCustom(from, todayIso);
  }

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div className="timeline-filters" role="tablist" aria-label="Time range" style={{ flexWrap: 'wrap' }}>
        {RANGE_PRESETS.map(preset => (
          <button
            key={preset.id}
            onClick={() => {
              setRangeError(null);
              onChange({ id: preset.id });
            }}
            className="filter-chip"
            style={selection.id === preset.id ? accent : undefined}
          >
            {preset.label}
          </button>
        ))}

        <button
          onClick={() => onChange({ id: CUSTOM_RANGE_ID, from: range.from, to: range.to })}
          className="filter-chip"
          style={isCustom ? accent : undefined}
        >
          Custom
        </button>
      </div>

      {isCustom && (
        <div
          className="chart-card"
          style={{ display: 'grid', gap: 12, padding: 14 }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: '#777', marginBottom: 4 }}>From</label>
              <input
                type="date"
                value={draftFrom}
                min={minIso}
                max={draftTo || todayIso}
                onChange={e => setDraftFrom(e.target.value)}
                style={rangeInputStyle}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: '#777', marginBottom: 4 }}>To</label>
              <input
                type="date"
                value={draftTo}
                min={draftFrom || minIso}
                max={todayIso}
                onChange={e => setDraftTo(e.target.value)}
                style={rangeInputStyle}
              />
            </div>

            <button
              onClick={() => applyCustom(draftFrom, draftTo)}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: 'none',
                background: '#0F4C75',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Apply dates
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#777' }}>or show the last</span>

            <input
              type="number"
              min={1}
              value={quickAmount}
              onChange={e => setQuickAmount(e.target.value)}
              style={{ ...rangeInputStyle, width: 70 }}
              aria-label="Amount"
            />

            <select
              value={quickUnit}
              onChange={e => setQuickUnit(e.target.value)}
              style={rangeInputStyle}
              aria-label="Unit"
            >
              {QUICK_UNITS.map(u => (
                <option key={u.id} value={u.id}>{u.label}</option>
              ))}
            </select>

            <button
              onClick={applyQuick}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: '1px solid #0F4C75',
                background: '#fff',
                color: '#0F4C75',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Apply
            </button>
          </div>

          {rangeError && <span style={{ fontSize: 12, color: '#B3261E' }}>{rangeError}</span>}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// METRIC CARD
// ============================================================================

function MetricCard({ metric, data, totalDays, active, onClick }) {
  const Icon = metric.icon;

  const trend = calculateTrend(data, metric.id);
  const coverage = calculateCoverage(data, metric.id, totalDays);
  const average = calculateAverage(data, metric.id);
  const cov = coverageTone(coverage.label);

  const latestRow = data[data.length - 1];

  const TrendIcon = trend.direction === 'up' ? TrendingUp : trend.direction === 'down' ? TrendingDown : Minus;

  const displayValue = metric.isBloodPressure
    ? formatBloodPressure(metric, latestRow)
    : formatValue(metric, trend.latest);

  return (
    <button
      onClick={onClick}
      className="metric-card"
      style={{
        borderColor: active ? metric.color : undefined,
        backgroundColor: active ? `${metric.color}0D` : undefined,
      }}
    >
      <div className="metric-card-top">
        <span
          className="metric-icon"
          style={{ backgroundColor: `${metric.color}1A`, color: metric.color }}
        >
          <Icon size={18} />
        </span>

        {!metric.isBloodPressure && (
          <span
            className="metric-trend"
            style={{
              color: trend.direction === 'up' ? '#993C1D' : trend.direction === 'down' ? '#0F6E56' : '#5F5E5A',
            }}
          >
            <TrendIcon size={13} />
            {trend.delta !== 0 ? `${trend.delta > 0 ? '+' : ''}${trend.delta}` : '—'}
          </span>
        )}
      </div>

      <p className="metric-label">{metric.label}</p>

      <p className="metric-value">
        {displayValue}
        <span className="metric-unit">{metric.unit}</span>
      </p>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 4 }}>
        <p className="metric-updated" style={{ margin: 0 }}>
          {metric.isBloodPressure
            ? 'Latest reading'
            : `Avg ${formatValue(metric, average)}`}
        </p>

        <span style={{ fontSize: 11, fontWeight: 600, color: cov.color }}>{coverage.percentage}%</span>
      </div>
    </button>
  );
}

// ============================================================================
// MAIN LINE CHART (renders two lines for blood_pressure)
// ============================================================================

function TimelineChart({ metric, data, granularity }) {
  const chartData = data.map(item => ({
    date: formatBucketLabel(item.date, granularity),
    fullDate: item.date,
    value: item[metric.id],
    secondary: metric.secondaryKey ? item[metric.secondaryKey] : undefined,
  }));

  const hasData = chartData.some(item => Number.isFinite(item.value));
  const pointCount = chartData.filter(item => Number.isFinite(item.value)).length;
  const pointNoun = granularity === 'day' ? 'days' : granularity === 'week' ? 'weeks' : 'months';

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div>
          <h3 className="chart-title">{metric.label} trend</h3>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: '#888780' }}>
            {metric.description} · {GRANULARITY_LABELS[granularity]}
          </p>
        </div>

        <span className="chart-count">
          {pointCount} {pointNoun}
        </span>
      </div>

      {!hasData ? (
        <p style={{ fontSize: 13, color: '#888780', padding: '24px 0', textAlign: 'center' }}>
          No data available for this date range.
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData} margin={{ top: 10, right: 15, left: -15, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F2" vertical={false} />

            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: '#888780' }}
              axisLine={false}
              tickLine={false}
              minTickGap={24}
            />

            <YAxis
              tick={{ fontSize: 11, fill: '#888780' }}
              axisLine={false}
              tickLine={false}
              domain={['dataMin - 2', 'dataMax + 2']}
            />

            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }}
              formatter={(value, name) => [
                `${formatValue(metric, value)} ${metric.unit}`,
                name === 'secondary' ? 'Diastolic' : metric.isBloodPressure ? 'Systolic' : metric.label,
              ]}
              labelFormatter={label => label}
            />

            <Line
              type="monotone"
              dataKey="value"
              stroke={metric.color}
              strokeWidth={2.5}
              dot={pointCount > 150 ? false : { r: 3, fill: metric.color }}
              activeDot={{ r: 6 }}
              connectNulls={false}
              isAnimationActive={pointCount <= 400}
            />

            {metric.isBloodPressure && (
              <Line
                type="monotone"
                dataKey="secondary"
                stroke="#888780"
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={pointCount > 150 ? false : { r: 2.5, fill: '#888780' }}
                activeDot={{ r: 5 }}
                connectNulls={false}
                isAnimationActive={pointCount <= 400}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ============================================================================
// AREA CHART
// ============================================================================

function MovementChart({ data, granularity }) {
  const chartData = data.map(item => ({
    date: formatBucketLabel(item.date, granularity),
    movement: item.movement,
  }));

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div>
          <h3 className="chart-title">Daily movement</h3>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: '#888780' }}>
            Movement measured from Smart Watch
            {granularity === 'day' ? '' : ` · ${GRANULARITY_LABELS[granularity].toLowerCase()} (steps per day)`}
          </p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={230}>
        <AreaChart data={chartData} margin={{ top: 10, right: 15, left: -15, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F2" vertical={false} />

          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#888780' }}
            axisLine={false}
            tickLine={false}
            minTickGap={24}
          />

          <YAxis tick={{ fontSize: 11, fill: '#888780' }} axisLine={false} tickLine={false} />

          <Tooltip formatter={value => [`${Math.round(value)} steps`, 'Movement']} />

          <Area
            type="monotone"
            dataKey="movement"
            stroke="#2A9D8F"
            fill="#2A9D8F"
            fillOpacity={0.12}
            strokeWidth={2}
            dot={false}
            isAnimationActive={chartData.length <= 400}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ============================================================================
// "SHOW MORE" FOOTER for long lists
// ============================================================================

function ShowMoreFooter({ shown, total, onMore, onAll }) {
  if (shown >= total) return null;

  const buttonStyle = {
    padding: '7px 14px',
    borderRadius: 8,
    border: '1px solid #E5E7EB',
    background: '#fff',
    color: '#0F4C75',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  };

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', padding: '14px 0 4px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, color: '#888780' }}>
        Showing {shown.toLocaleString()} of {total.toLocaleString()}
      </span>
      <button onClick={onMore} style={buttonStyle}>Show {LIST_PAGE_SIZE} more</button>
      <button onClick={onAll} style={buttonStyle}>Show all</button>
    </div>
  );
}

// ============================================================================
// FULL DATA TABLE
// ============================================================================

function FullDataTable({ data }) {
  const [visible, setVisible] = useState(LIST_PAGE_SIZE);

  // New range -> start from the first page again.
  useEffect(() => {
    setVisible(LIST_PAGE_SIZE);
  }, [data]);

  const rows = useMemo(() => [...data].reverse(), [data]);

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div>
          <h3 className="chart-title">Full health data</h3>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: '#888780' }}>Daily Smart Watch readings</p>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
          <thead>
            <tr>
              {['Date', 'HR', 'RR', 'HRV', 'Skin Temp', 'Movement', 'Energy', 'BP'].map(header => (
                <th
                  key={header}
                  style={{
                    textAlign: 'left',
                    padding: '11px 10px',
                    fontSize: 11,
                    color: '#777',
                    borderBottom: '1px solid #EEF0F2',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.slice(0, visible).map((item, index) => (
              <tr key={`${item.date}-${index}`}>
                <td
                  style={{
                    padding: '10px',
                    fontSize: 12,
                    fontWeight: 600,
                    borderBottom: '1px solid #F2F3F4',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatLongDate(item.date)}
                </td>

                <td className="health-table-value">
                  {Number.isFinite(item.heart_rate) ? `${item.heart_rate} bpm` : '--'}
                </td>

                <td className="health-table-value">
                  {Number.isFinite(item.respiratory_rate) ? `${item.respiratory_rate} brpm` : '--'}
                </td>

                <td className="health-table-value">
                  {Number.isFinite(item.hrv) ? `${item.hrv} ms` : '--'}
                </td>

                <td className="health-table-value">
                  {Number.isFinite(item.skin_temperature) ? `${item.skin_temperature} °C` : '--'}
                </td>

                <td className="health-table-value">
                  {Number.isFinite(item.movement) ? `${item.movement.toLocaleString()} steps` : '--'}
                </td>

                <td className="health-table-value">
                  {Number.isFinite(item.active_energy) ? `${item.active_energy} kcal` : '--'}
                </td>

                <td className="health-table-value">
                  {Number.isFinite(item.blood_pressure)
                    ? `${item.blood_pressure}/${item.blood_pressure_diastolic ?? '--'} mmHg`
                    : '--'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && (
        <p style={{ fontSize: 13, color: '#888780', padding: '20px 0', textAlign: 'center' }}>
          No readings in this date range.
        </p>
      )}

      <ShowMoreFooter
        shown={Math.min(visible, rows.length)}
        total={rows.length}
        onMore={() => setVisible(v => v + LIST_PAGE_SIZE)}
        onAll={() => setVisible(rows.length)}
      />
    </div>
  );
}

// ============================================================================
// TIMELINE LIST
// ============================================================================

function TimelineList({ data }) {
  const [visible, setVisible] = useState(LIST_PAGE_SIZE);

  useEffect(() => {
    setVisible(LIST_PAGE_SIZE);
  }, [data]);

  const entries = useMemo(() => {
    const list = [];

    data.forEach(day => {
      METRICS.forEach(metric => {
        if (metric.isBloodPressure) {
          const systolic = day[metric.id];
          const diastolic = day[metric.secondaryKey];
          if (Number.isFinite(systolic)) {
            list.push({
              id: `${metric.id}-${day.date}`,
              metric,
              display: Number.isFinite(diastolic) ? `${systolic}/${diastolic}` : `${systolic}`,
              date: day.date,
            });
          }
          return;
        }

        const value = day[metric.id];
        if (Number.isFinite(value)) {
          list.push({
            id: `${metric.id}-${day.date}`,
            metric,
            display: `${formatValue(metric, value)}`,
            date: day.date,
          });
        }
      });
    });

    list.sort((a, b) => b.date.localeCompare(a.date));
    return list;
  }, [data]);

  return (
    <div className="timeline-list">
      <div className="timeline-list-header">
        <h3>All synced readings</h3>
        <span style={{ fontSize: 12, color: '#888780' }}>{entries.length.toLocaleString()} readings</span>
      </div>

      {entries.length === 0 ? (
        <p style={{ fontSize: 13, color: '#888780', padding: '20px' }}>No health data available for this date range.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {entries.slice(0, visible).map(entry => {
            const Icon = entry.metric.icon;

            return (
              <li key={entry.id} className="timeline-item">
                <span
                  className="timeline-icon"
                  style={{ backgroundColor: `${entry.metric.color}1A`, color: entry.metric.color }}
                >
                  <Icon size={14} />
                </span>

                <div style={{ minWidth: 0, flex: 1 }}>
                  <p className="timeline-text">
                    {entry.metric.label}:{' '}
                    <span className="timeline-value">
                      {entry.display} {entry.metric.unit}
                    </span>
                  </p>

                  <p className="timeline-meta">{formatLongDate(entry.date)} · Smart Watch</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <ShowMoreFooter
        shown={Math.min(visible, entries.length)}
        total={entries.length}
        onMore={() => setVisible(v => v + LIST_PAGE_SIZE)}
        onAll={() => setVisible(entries.length)}
      />
    </div>
  );
}

// ============================================================================
// TRAJECTORY SUMMARY
// ============================================================================

function TrajectorySummary({ summary, hasData }) {
  // The backend's summary can return a default trajectory even with zero real
  // readings behind it, so this also requires hasData before rendering
  // anything but an honest "not enough data yet" message.
  if (!summary || !hasData) {
    return (
      <div className="chart-card">
        <div className="chart-header">
          <h3 className="chart-title">Health trend summary</h3>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: '#888780' }}>
          Not enough data yet to compute a trend summary. Sync your device or log a reading above to see this.
        </p>
      </div>
    );
  }

  const rows = [
    { key: 'regulation', state: summary.trajectory.regulation },
    { key: 'repair', state: summary.trajectory.repair },
    { key: 'integration', state: summary.trajectory.integration },
  ];

  return (
    <div className="chart-card">
      <div className="chart-header">
        <h3 className="chart-title">Health trend summary</h3>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 10,
          marginBottom: 16,
        }}
      >
        {rows.map(row => {
          const tone = trajectoryTone(row.state);

          return (
            <div key={row.key} style={{ border: '1px solid #EEF0F2', borderRadius: 10, padding: '12px' }}>
              <p style={{ margin: 0, fontSize: 12, color: '#888780' }}>{TRAJECTORY_LABELS[row.key]}</p>
              <p style={{ margin: '5px 0 0', fontSize: 14, fontWeight: 600, color: tone.color }}>
                {tone.label}
              </p>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'grid', gap: 10 }}>
        {summary.positives?.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <ShieldCheck size={15} color="#0F6E56" style={{ marginTop: 2, flexShrink: 0 }} />

            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13, color: '#3A3A38' }}>
              {summary.positives.map((text, i) => <li key={i}>{text}</li>)}
            </ul>
          </div>
        )}

        {summary.watchouts?.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <AlertTriangle size={15} color="#B4780A" style={{ marginTop: 2, flexShrink: 0 }} />

            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13, color: '#3A3A38' }}>
              {summary.watchouts.map((text, i) => <li key={i}>{text}</li>)}
            </ul>
          </div>
        )}
      </div>

      <p style={{ fontSize: 12, color: '#888780', marginTop: 14, marginBottom: 0 }}>
        This dashboard displays wearable trends and is not a medical diagnosis.
      </p>
    </div>
  );
}

// ============================================================================
// REPORTS — real upload/list/delete backed by /api/health-reports
// ============================================================================

function ReportsTab() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchReports()
      .then(rows => setReports(rows || []))
      .catch(err => {
        console.error('Reports fetch failed:', err);
        setError(err.message || 'Could not load reports');
        setReports([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleFileSelected(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    setUploading(true);
    setUploadError(null);

    try {
      const newReport = await uploadReport(file);
      setReports(prev => [newReport, ...prev]);
    } catch (err) {
      setUploadError(err.message || 'Failed to upload report');
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this report? This can\'t be undone.')) return;

    setDeletingId(id);
    try {
      await deleteReport(id);
      setReports(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      setError(err.message || 'Failed to delete report');
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="section-stack">
      <div>
        <h2 style={{ margin: '0 0 5px', fontSize: 18 }}>Health reports</h2>
        <p style={{ margin: 0, fontSize: 13, color: '#888780' }}>Upload and manage your health and genetic reports.</p>
      </div>

      <div className="chart-card">
        <div className="chart-header">
          <div>
            <h3 className="chart-title">Upload a report</h3>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: '#888780' }}>
              PDF, image, or Word document — up to 25MB.
            </p>
          </div>
        </div>

        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '9px 16px',
            borderRadius: 8,
            border: '1px dashed #0F4C75',
            color: '#0F4C75',
            fontSize: 13,
            fontWeight: 600,
            cursor: uploading ? 'default' : 'pointer',
            opacity: uploading ? 0.7 : 1,
          }}
        >
          {uploading ? (
            <Loader2 size={15} style={{ animation: 'health-spin 1s linear infinite' }} />
          ) : (
            <UploadCloud size={15} />
          )}
          {uploading ? 'Uploading…' : 'Choose file'}
          <input
            type="file"
            onChange={handleFileSelected}
            disabled={uploading}
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            style={{ display: 'none' }}
          />
        </label>

        {uploadError && (
          <p style={{ margin: '8px 0 0', fontSize: 12, color: '#B3261E' }}>{uploadError}</p>
        )}
      </div>

      {error && (
        <div className="chart-card" style={{ borderColor: '#B3261E', backgroundColor: '#B3261E0D' }}>
          <p style={{ margin: 0, fontSize: 13, color: '#B3261E' }}>{error}</p>
        </div>
      )}

      <div className="reports-list">
        {loading ? (
          <div className="chart-card" style={{ textAlign: 'center', padding: '30px 0', color: '#888780' }}>
            <Loader2 size={18} style={{ marginBottom: 6, animation: 'health-spin 1s linear infinite' }} />
            <p style={{ margin: 0, fontSize: 13 }}>Loading reports…</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="chart-card" style={{ textAlign: 'center', padding: '30px 0', color: '#888780' }}>
            <p style={{ margin: 0, fontSize: 13 }}>No reports uploaded yet.</p>
          </div>
        ) : (
          reports.map(report => (
            <div key={report.id} className="report-card">
              <div className="report-card-left">
                <span className="report-icon">
                  <FileText size={18} />
                </span>

                <div>
                  <p className="report-name">{report.original_name}</p>
                  <p className="report-meta">
                    Uploaded {formatUploadedAt(report.uploaded_at)} · {formatFileSize(report.size_bytes)}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <a
                  href={`${config.baseURL}/uploads/${report.file_path}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open report"
                  style={{ color: '#0F4C75', display: 'flex', alignItems: 'center' }}
                >
                  <ExternalLink size={16} />
                </a>

                <button
                  onClick={() => handleDelete(report.id)}
                  disabled={deletingId === report.id}
                  aria-label="Delete report"
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: deletingId === report.id ? 'default' : 'pointer',
                    color: '#B3261E',
                    display: 'flex',
                    alignItems: 'center',
                    padding: 0,
                  }}
                >
                  {deletingId === report.id ? (
                    <Loader2 size={16} style={{ animation: 'health-spin 1s linear infinite' }} />
                  ) : (
                    <Trash2 size={16} />
                  )}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================================================
// DEVICES
// ============================================================================

function DevicesTab({ hasData }) {
  return (
    <div className="reports-list">
      {DEVICE_CATALOG.map(device => {
        const Icon = device.icon;

        return (
          <div key={device.id} className="device-card">
            <div className="device-card-left">
              <span className="device-icon">
                <Icon size={18} />
              </span>

              <div>
                <p className="device-name">{device.name}</p>
                <p className="device-desc">{device.desc}</p>
              </div>
            </div>

            <span className={`connect-btn ${hasData ? 'connected' : ''}`}>
              {hasData && <Check size={13} />}
              {hasData ? 'Connected' : 'Not connected'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// HEALTH STATISTICS
// ============================================================================

function HealthStatistics({ data, totalDays }) {
  const stats = METRICS.map(metric => ({
    metric,
    average: calculateAverage(data, metric.id),
    coverage: calculateCoverage(data, metric.id, totalDays),
  }));

  return (
    <div className="chart-card">
      <div className="chart-header">
        <h3 className="chart-title">Health statistics</h3>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
        }}
      >
        {stats.map(item => {
          const Icon = item.metric.icon;

          return (
            <div key={item.metric.id} style={{ border: '1px solid #EEF0F2', borderRadius: 10, padding: 13 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    width: 30,
                    height: 30,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 8,
                    backgroundColor: `${item.metric.color}1A`,
                    color: item.metric.color,
                  }}
                >
                  <Icon size={16} />
                </span>

                <span style={{ fontSize: 12, color: '#666' }}>{item.metric.shortLabel}</span>
              </div>

              <p style={{ margin: '12px 0 2px', fontSize: 20, fontWeight: 700 }}>
                {item.metric.isBloodPressure ? '--' : formatValue(item.metric, item.average)}{' '}
                <span style={{ fontSize: 11, fontWeight: 500, color: '#888' }}>{item.metric.unit}</span>
              </p>

              <p style={{ margin: 0, fontSize: 11, color: '#888' }}>
                {item.metric.isBloodPressure ? 'See latest reading above' : 'Average'} · {item.coverage.percentage}%
                coverage
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// STATUS PILL
// ============================================================================

function StatusPill({ loading, error }) {
  if (loading) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '7px 10px',
          borderRadius: 8,
          background: '#88878012',
          color: '#5F5E5A',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        <Loader2 size={14} style={{ animation: 'health-spin 1s linear infinite' }} />
        Syncing...
      </span>
    );
  }

  if (error) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '7px 10px',
          borderRadius: 8,
          background: '#B3261E12',
          color: '#B3261E',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        <AlertTriangle size={14} />
        Connection error
      </span>
    );
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '7px 10px',
        borderRadius: 8,
        background: '#0F6E5612',
        color: '#0F6E56',
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      <Check size={14} />
      Live data
    </span>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

// Inline keyframes so the loading spinner works without a Health.css change.
const SPIN_KEYFRAMES = '@keyframes health-spin { to { transform: rotate(360deg); } }';

const Health = () => {
  const [tab, setTab] = useState('Overview');
  const [activeMetric, setActiveMetric] = useState('heart_rate');
  // { id: '7d' | '1m' | … | 'custom', from?, to? }
  const [rangeSel, setRangeSel] = useState({ id: '7d' });
  const [activeAlert, setActiveAlert] = useState(null);
  const navigate = useNavigate();

  // Tabs depend on the user's family planning goal (see constants/familyPlanningGoals.js).
  // No goal chosen yet -> every tab is shown.
  //
  // The goal is read from the server every time this page opens, so a goal
  // changed on the profile page is always used — even if that page didn't
  // update Redux. Redux's value is only used until the server answers.
  const dispatch = useDispatch();
  const reduxToken = useSelector((state) => state.user?.token);
  const reduxGoal = useSelector((state) => state.user?.userInfo?.family_planning_goal) || null;
  const [serverGoal, setServerGoal] = useState(undefined); // undefined = not loaded yet

  useEffect(() => {
    const token = reduxToken || getAuthToken();
    if (!token) return undefined;
    let cancelled = false;

    fetch(`${config.baseURL}/api/onboarding/status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (cancelled || !body) return;
        const fresh = body.data?.family_planning_goal || null;
        setServerGoal(fresh);
        // keep Redux (profile, dashboard) in step with the server
        if (fresh !== reduxGoal) dispatch(updateUserInfo({ family_planning_goal: fresh }));
      })
      .catch(() => { /* keep using the Redux value */ });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxToken]);

  const goal = serverGoal !== undefined ? serverGoal : reduxGoal;
  const showCoupleDna = goalHasFeature(goal, 'couple_dna');
  const visibleTabs = useMemo(
    () => TABS.filter((t) => t !== COUPLE_DNA_TAB || showCoupleDna),
    [showCoupleDna]
  );

  // If the goal changes and the open tab is no longer allowed, go back to Overview
  useEffect(() => {
    if (!visibleTabs.includes(tab)) setTab('Overview');
  }, [visibleTabs, tab]);

  // ---- Date range -> API request window -> client-side trim -> aggregation ----
  const range = useMemo(() => buildRange(rangeSel), [rangeSel]);

  const { data: fetchedData, summary, loading, error, refetch } = useHealthData(range.days);

  // The summary endpoint returns "last N days from today"; trim to the exact
  // from/to the person picked (matters for custom ranges that end before today).
  const filteredData = useMemo(
    () => fetchedData.filter(row => {
      const d = String(row.date).slice(0, 10);
      return d >= range.from && d <= range.to;
    }),
    [fetchedData, range.from, range.to]
  );

  const granularity = useMemo(() => pickGranularity(range.totalDays), [range.totalDays]);

  // Charts use bucketed data so 30 years stays fast and readable; cards,
  // alerts, tables and lists keep working from the raw daily rows.
  const chartData = useMemo(() => aggregateData(filteredData, granularity), [filteredData, granularity]);

  const activeMetricConfig = METRICS.find(metric => metric.id === activeMetric) || METRICS[0];

  const overallCoverage = useMemo(() => {
    if (!filteredData.length) return 0;
    const pcts = METRICS.map(m => calculateCoverage(filteredData, m.id, range.totalDays).percentage);
    return Math.round(pcts.reduce((s, p) => s + p, 0) / pcts.length);
  }, [filteredData, range.totalDays]);

  const latestData = filteredData[filteredData.length - 1];

  // Surface an alert if the most recent HR or BP reading is out of range.
  useEffect(() => {
    if (!latestData) return;

    const hrAlert = localAlertCheck(METRICS.find(m => m.id === 'heart_rate'), latestData);
    const bpAlert = localAlertCheck(METRICS.find(m => m.id === 'blood_pressure'), latestData);

    const alert = bpAlert || hrAlert;
    if (alert) setActiveAlert(alert);
  }, [latestData]);

  const handleLogged = alert => {
    if (alert) setActiveAlert(alert);
    refetch();
  };

  return (
    <div className="health-page">
      <style>{SPIN_KEYFRAMES}</style>

      {/* ================================================================
          HEADER
      ================================================================= */}

      <div className="health-header">
        <div className="health-header-inner">
          <div>
            <h1 className="health-title">Health monitoring</h1>
            <p className="health-subtitle">Smart Watch health data synced through Health App</p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <StatusPill loading={loading} error={error} />
          </div>
        </div>
      </div>

      {/* ================================================================
          MAIN CONTAINER
      ================================================================= */}

      <div className="health-container">
        {activeAlert && (
          <div style={{ marginBottom: 14 }}>
            <AlertBanner alert={activeAlert} onDismiss={() => setActiveAlert(null)} />
          </div>
        )}

        {error && (
          <div
            className="chart-card"
            style={{ borderColor: '#B3261E', backgroundColor: '#B3261E0D', marginBottom: 14 }}
          >
            <p style={{ margin: 0, fontSize: 13, color: '#B3261E' }}>
              Couldn't load health data: {error}.{' '}
              <button
                onClick={refetch}
                style={{ border: 'none', background: 'none', color: '#B3261E', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: 13 }}
              >
                Retry
              </button>
            </p>
          </div>
        )}

        {/* ==============================================================
            TABS
        =============================================================== */}

        <div className="health-tabs">
          {visibleTabs.map(tabName => (
            <button
              key={tabName}
              onClick={() => setTab(tabName)}
              className={`health-tab ${tab === tabName ? 'active' : ''}`}
            >
              {tabName}
            </button>
          ))}
        </div>

        {/* ==============================================================
            DATE RANGE SELECTOR
        =============================================================== */}

        {(tab === 'Overview' || tab === 'Timeline') && (
          <div style={{ margin: '14px 0 4px', display: 'grid', gap: 8 }}>
            <RangeSelector selection={rangeSel} range={range} onChange={setRangeSel} />

            <span style={{ fontSize: 12, color: '#888780' }}>
              {loading
                ? 'Loading…'
                : `${formatRangeDate(range.from)} – ${formatRangeDate(range.to)} · ${filteredData.length.toLocaleString()} of ${range.totalDays.toLocaleString()} days with data · ${overallCoverage}% overall coverage${granularity === 'day' ? '' : ` · charts show ${GRANULARITY_LABELS[granularity].toLowerCase()}`}`}
            </span>
          </div>
        )}

        {/* ==============================================================
            CONTENT
        =============================================================== */}

        <div className="tab-content">
          {/* ============================================================
              OVERVIEW
          ============================================================= */}

          {tab === 'Overview' && (
            <div className="section-stack">
              {loading && !filteredData.length ? (
                <div className="chart-card" style={{ textAlign: 'center', padding: '40px 0', color: '#888780' }}>
                  <Loader2 size={20} style={{ marginBottom: 8, animation: 'health-spin 1s linear infinite' }} />
                  <p style={{ margin: 0, fontSize: 13 }}>Loading health data…</p>
                </div>
              ) : (
                <>
                  <div className="metric-grid">
                    {METRICS.map(metric => (
                      <MetricCard
                        key={metric.id}
                        metric={metric}
                        data={filteredData}
                        totalDays={range.totalDays}
                        active={activeMetric === metric.id}
                        onClick={() => setActiveMetric(metric.id)}
                      />
                    ))}
                  </div>

                  <TimelineChart metric={activeMetricConfig} data={chartData} granularity={granularity} />

                  <MovementChart data={chartData} granularity={granularity} />

                  <QuickLogBloodPressure onLogged={handleLogged} />

                  <HealthStatistics data={filteredData} totalDays={range.totalDays} />

                  <TrajectorySummary summary={summary} hasData={filteredData.length > 0} />

                  {latestData && (
                    <div className="chart-card">
                      <div className="chart-header">
                        <div>
                          <h3 className="chart-title">Latest synced data</h3>
                          <p style={{ margin: '3px 0 0', fontSize: 12, color: '#888780' }}>
                            {formatLongDate(latestData.date)}
                          </p>
                        </div>

                        <span style={{ fontSize: 12, color: '#0F6E56', fontWeight: 600 }}>Apple Watch</span>
                      </div>

                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                          gap: 10,
                        }}
                      >
                        {METRICS.map(metric => {
                          const Icon = metric.icon;

                          return (
                            <div
                              key={metric.id}
                              style={{ border: '1px solid #EEF0F2', borderRadius: 10, padding: '12px' }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                <Icon size={15} color={metric.color} />
                                <span style={{ fontSize: 11, color: '#777' }}>{metric.shortLabel}</span>
                              </div>

                              <p style={{ margin: '8px 0 0', fontSize: 18, fontWeight: 700 }}>
                                {metric.isBloodPressure
                                  ? formatBloodPressure(metric, latestData)
                                  : formatValue(metric, latestData[metric.id])}
                                <span style={{ fontSize: 11, marginLeft: 4, fontWeight: 500, color: '#888' }}>
                                  {metric.unit}
                                </span>
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <button onClick={() => setTab('Timeline')} className="view-more-link">
                    View full timeline
                    <ChevronRight size={15} />
                  </button>
                </>
              )}
            </div>
          )}

          {/* ============================================================
              TIMELINE
          ============================================================= */}

          {tab === 'Timeline' && (
            <div className="section-stack">
              <div className="timeline-filters">
                {METRICS.map(metric => (
                  <button
                    key={metric.id}
                    onClick={() => setActiveMetric(metric.id)}
                    className="filter-chip"
                    style={{
                      borderColor: activeMetric === metric.id ? metric.color : undefined,
                      color: activeMetric === metric.id ? metric.color : undefined,
                      backgroundColor: activeMetric === metric.id ? `${metric.color}0D` : undefined,
                    }}
                  >
                    {metric.shortLabel}
                  </button>
                ))}
              </div>

              <TimelineChart metric={activeMetricConfig} data={chartData} granularity={granularity} />

              <TimelineList data={filteredData} />

              <FullDataTable data={filteredData} />
            </div>
          )}

          {/* ============================================================
              REPORTS
          ============================================================= */}

          {tab === 'Reports' && <ReportsTab />}

          {/* ============================================================
              COUPLE DNA — only for goals that include it
          ============================================================= */}

          {tab === COUPLE_DNA_TAB && showCoupleDna && (
            <CoupleGeneticTab onNavigateToAppointments={() => navigate('/appointment')} />
          )}

          {/* ============================================================
              CONNECTED DEVICES
          ============================================================= */}

          {tab === 'Connected devices' && (
            <div className="section-stack">
              <div>
                <h2 style={{ margin: '0 0 5px', fontSize: 18 }}>Connected devices</h2>
                <p style={{ margin: 0, fontSize: 13, color: '#888780' }}>Your health data source chain.</p>
              </div>

              <div className="chart-card">
                <div className="chart-header">
                  <h3 className="chart-title">Health data flow</h3>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 15,
                    flexWrap: 'wrap',
                    padding: '20px 0',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: 55,
                        height: 55,
                        margin: '0 auto 7px',
                        borderRadius: 14,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#0F4C7512',
                        color: '#0F4C75',
                      }}
                    >
                      <Watch size={25} />
                    </div>

                    <strong style={{ fontSize: 12 }}>Apple Watch</strong>
                    <p style={{ margin: '3px 0 0', fontSize: 10, color: '#888' }}>Sensors</p>
                  </div>

                  <ChevronRight color="#AAA" />

                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: 55,
                        height: 55,
                        margin: '0 auto 7px',
                        borderRadius: 14,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#0F6E5612',
                        color: '#0F6E56',
                      }}
                    >
                      <Smartphone size={25} />
                    </div>

                    <strong style={{ fontSize: 12 }}>iPhone</strong>
                    <p style={{ margin: '3px 0 0', fontSize: 10, color: '#888' }}>HealthKit</p>
                  </div>

                  <ChevronRight color="#AAA" />

                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: 55,
                        height: 55,
                        margin: '0 auto 7px',
                        borderRadius: 14,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#8B5CF612',
                        color: '#8B5CF6',
                      }}
                    >
                      <Activity size={25} />
                    </div>

                    <strong style={{ fontSize: 12 }}>Health dashboard</strong>
                    <p style={{ margin: '3px 0 0', fontSize: 10, color: '#888' }}>Graphs & trends</p>
                  </div>
                </div>
              </div>

              <DevicesTab hasData={filteredData.length > 0} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Health;